import { useEffect, useRef, useState } from "react";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";
import { Eye, Trash } from "lucide-react";

import useBanner from "../../../../services/Inventory/useBanner";
import NoIndex from "../../../../components/NoIndex";

function BannerLibrary() {
  const { fetchBanner, updateBannerStatus, deleteBanner } = useBanner();

  const [fetchedBanner, setFetchedBanner] = useState([]);

  const initialFetchRef = useRef(false);

  const [previewHTML, setPreviewHTML] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    item: null,
  });

  const [isDeleting, setIsDeleting] = useState(false);

  const [updatingIds, setUpdatingIds] = useState(new Set());

  const ITEMS_PER_PAGE = 8;

  const [currentPage, setCurrentPage] = useState(1);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

  const currentItems = fetchedBanner.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const totalPages = Math.ceil(fetchedBanner.length / ITEMS_PER_PAGE);

  /* =========================================================
     FETCH BANNERS
  ========================================================= */

  useEffect(() => {
    if (initialFetchRef.current) return;

    initialFetchRef.current = true;

    fetchBanner(setFetchedBanner);
  }, []);

  /* =========================================================
     PAGE CORRECTION
  ========================================================= */

  useEffect(() => {
    if (totalPages === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }

      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  /* =========================================================
     PREVIEW MODAL
  ========================================================= */

  const openPreview = (html) => {
    setPreviewHTML(html || "");
    setShowModal(true);
  };

  const closePreview = () => {
    setShowModal(false);
    setPreviewHTML("");
  };

  const getPreviewHTML = (html = "") => {
    if (!html) return "";

    const previewStyle = `
    <style id="banner-library-preview-style">

      html,
      body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: 100% !important;
        overflow: hidden !important;
        background: transparent !important;
      }

      body {
        position: relative !important;
        width: 100% !important;
        height: 100% !important;
      }

      .bannerrs {
        position: relative !important;

        width: 100% !important;
        height: 100% !important;

        margin: 0 !important;
        padding: 0 !important;

        overflow: hidden !important;

        border-radius: 0 !important;

        background: transparent !important;
      }

      .banner-preview-slide {
        position: absolute !important;

        left: 0 !important;
        top: 0 !important;

        margin: 0 !important;

        transform-origin: top left !important;

        overflow: hidden !important;
      }

      .banner-preview-dots {
        position: absolute;

        left: 50%;
        bottom: 3px;

        transform: translateX(-50%);

        display: flex;
        align-items: center;
        justify-content: center;

        gap: 4px;

        z-index: 999999;

        pointer-events: none;
      }

      .banner-preview-dot {
        width: 5px;
        height: 5px;

        border-radius: 50%;

        background: rgba(0, 0, 0, 0.25);
      }

      .banner-preview-dot.active {
        width: 12px;

        border-radius: 10px;

        background: rgba(0, 0, 0, 0.65);
      }

      html::-webkit-scrollbar,
      body::-webkit-scrollbar,
      *::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }

      html,
      body,
      * {
        scrollbar-width: none !important;
      }

    </style>
  `;

    const previewScript = `
    <script>
      (function () {

        var banner = null;
        var slides = [];
        var dots = [];

        var currentIndex = 0;
        var interval = null;

        /* =========================================
           GET REAL ORIGINAL SIZE
        ========================================= */

        function saveOriginalSize(slide) {

          slide.style.transform = "none";

          var computed =
            window.getComputedStyle(slide);

          var width =
            slide.offsetWidth ||
            parseFloat(computed.width) ||
            300;

          var height =
            slide.offsetHeight ||
            parseFloat(computed.height) ||
            200;

          slide.setAttribute(
            "data-preview-width",
            width
          );

          slide.setAttribute(
            "data-preview-height",
            height
          );
        }

        function fitSlide(slide) {

          if (!slide) return;

          var originalWidth =
            Number(
              slide.getAttribute(
                "data-preview-width"
              )
            ) || 300;

          var originalHeight =
            Number(
              slide.getAttribute(
                "data-preview-height"
              )
            ) || 200;

          var availableWidth =
            document.documentElement.clientWidth;

          var availableHeight =
            document.documentElement.clientHeight;

          var dotSpace =
            slides.length > 1
              ? 9
              : 0;

          availableHeight =
            Math.max(
              1,
              availableHeight - dotSpace
            );

          var scaleX =
            availableWidth /
            originalWidth;

          var scaleY =
            availableHeight /
            originalHeight;

          slide.style.left = "0px";
          slide.style.top = "0px";

          slide.style.width =
            originalWidth + "px";

          slide.style.height =
            originalHeight + "px";

          slide.style.transform =
            "scale(" +
            scaleX +
            "," +
            scaleY +
            ")";

          slide.style.transformOrigin =
            "top left";
        }

        /* =========================================
           SHOW ONE CAROUSEL SLIDE
        ========================================= */

        function showSlide(index) {

          if (!slides.length) {
            return;
          }

          currentIndex =
            (index + slides.length) %
            slides.length;

          slides.forEach(
            function (
              slide,
              slideIndex
            ) {

              if (
                slideIndex ===
                currentIndex
              ) {

                slide.style.display =
                  "block";

                slide.style.visibility =
                  "visible";

                slide.style.opacity =
                  "1";

                fitSlide(slide);

              } else {

                slide.style.display =
                  "none";

                slide.style.visibility =
                  "hidden";

                slide.style.opacity =
                  "0";
              }
            }
          );

          dots.forEach(
            function (
              dot,
              dotIndex
            ) {

              if (
                dotIndex ===
                currentIndex
              ) {
                dot.classList.add(
                  "active"
                );
              } else {
                dot.classList.remove(
                  "active"
                );
              }
            }
          );
        }

        /* =========================================
           DOTS
        ========================================= */

        function createDots() {

          if (
            !banner ||
            slides.length <= 1
          ) {
            return;
          }

          var wrapper =
            document.createElement(
              "div"
            );

          wrapper.className =
            "banner-preview-dots";

          slides.forEach(
            function (_, index) {

              var dot =
                document.createElement(
                  "span"
                );

              dot.className =
                "banner-preview-dot";

              if (index === 0) {
                dot.classList.add(
                  "active"
                );
              }

              wrapper.appendChild(
                dot
              );

              dots.push(dot);
            }
          );

          banner.appendChild(
            wrapper
          );
        }

        /* =========================================
           AUTO CAROUSEL
        ========================================= */

        function startCarousel() {

          if (
            slides.length <= 1
          ) {
            return;
          }

          if (interval) {
            clearInterval(interval);
          }

          interval =
            setInterval(
              function () {

                showSlide(
                  currentIndex + 1
                );

              },
              2500
            );
        }

        /* =========================================
           INIT
        ========================================= */

        function initPreview() {

          banner =
            document.querySelector(
              ".bannerrs"
            );

          if (!banner) {
            return;
          }

          slides =
            Array.prototype
              .slice.call(
                banner.children
              )
              .filter(
                function (element) {

                  return (
                    element.nodeType === 1 &&
                    !element.classList.contains(
                      "banner-preview-dots"
                    )
                  );
                }
              );

          if (!slides.length) {
            return;
          }

          slides.forEach(
            function (slide) {

              slide.classList.add(
                "banner-preview-slide"
              );

              saveOriginalSize(
                slide
              );
            }
          );

          createDots();

          showSlide(0);

          startCarousel();

          var images =
            document.querySelectorAll(
              "img"
            );

          images.forEach(
            function (image) {

              image.addEventListener(
                "load",
                function () {

                  slides.forEach(
                    function (
                      slide
                    ) {

                      saveOriginalSize(
                        slide
                      );
                    }
                  );

                  showSlide(
                    currentIndex
                  );
                }
              );
            }
          );

          setTimeout(
            function () {

              slides.forEach(
                function (slide) {
                  saveOriginalSize(
                    slide
                  );
                }
              );

              showSlide(
                currentIndex
              );

            },
            100
          );

          setTimeout(
            function () {

              showSlide(
                currentIndex
              );

            },
            300
          );
        }

        /* =========================================
           RESIZE
        ========================================= */

        window.addEventListener(
          "resize",
          function () {

            showSlide(
              currentIndex
            );

          }
        );

        /* =========================================
           START
        ========================================= */

        if (
          document.readyState ===
          "complete"
        ) {

          initPreview();

        } else {

          window.addEventListener(
            "load",
            initPreview
          );
        }

      })();
    <\/script>
  `;

    let updatedHTML = html;

    /* =============================================
     ADD PREVIEW CSS
  ============================================= */

    if (updatedHTML.toLowerCase().includes("</head>")) {
      updatedHTML = updatedHTML.replace(/<\/head>/i, `${previewStyle}</head>`);
    } else {
      updatedHTML = previewStyle + updatedHTML;
    }

    /* =============================================
     ADD CAROUSEL SCRIPT
  ============================================= */

    if (updatedHTML.toLowerCase().includes("</body>")) {
      updatedHTML = updatedHTML.replace(/<\/body>/i, `${previewScript}</body>`);
    } else {
      updatedHTML += previewScript;
    }

    return updatedHTML;
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleConfirmDelete = async () => {
    const id = deleteModal?.item?.id;

    if (!id || isDeleting) {
      return;
    }

    setIsDeleting(true);

    try {
      const success = await deleteBanner(id);

      if (!success) {
        return;
      }

      setFetchedBanner((prev) => prev.filter((banner) => banner.id !== id));

      setDeleteModal({
        show: false,
        item: null,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /* =========================================================
     STATUS UPDATE
  ========================================================= */

  const handleToggleStatus = async (item) => {
    const id = item?.id;

    if (!id) return;

    if (updatingIds.has(id)) {
      return;
    }

    const currentStatus = item?.card_status;

    const newStatus = currentStatus === "Y" ? "N" : "Y";

    setUpdatingIds((prev) => {
      const updated = new Set(prev);

      updated.add(id);

      return updated;
    });

    /* =====================================================
       OPTIMISTIC UPDATE
    ===================================================== */

    setFetchedBanner((prev) =>
      prev.map((banner) =>
        banner.id === id
          ? {
              ...banner,
              card_status: newStatus,
            }
          : banner,
      ),
    );

    try {
      const success = await updateBannerStatus(item, newStatus);

      if (!success) {
        setFetchedBanner((prev) =>
          prev.map((banner) =>
            banner.id === id
              ? {
                  ...banner,
                  card_status: currentStatus,
                }
              : banner,
          ),
        );
      }
    } finally {
      setUpdatingIds((prev) => {
        const updated = new Set(prev);

        updated.delete(id);

        return updated;
      });
    }
  };

  return (
    <>
      <NoIndex />

      <div className="w-full">

        <div className="flex w-full items-center justify-end">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              className="flex h-8 w-8 items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SlArrowLeft />
            </button>

            <button
              type="button"
              disabled={totalPages === 0 || currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              className="flex h-8 w-8 items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SlArrowRight />
            </button>
          </div>
        </div>

        {/* =================================================
            EMPTY
        ================================================= */}

        {currentItems.length === 0 && (
          <div className="flex min-h-[180px] items-center justify-center rounded-lg border border-dashed border-gray-300">
            <p className="text-sm text-gray-500">No banners found</p>
          </div>
        )}

        {currentItems.length > 0 && (
          <div className="grid grid-cols-1 gap-4 montserrat-font-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {currentItems.map((item) => {
              const isUpdating = updatingIds.has(item.id);

              return (
                <div
                  key={item.id}
                  className="rounded-lg border bg-white p-3 shadow-sm montserrat-font-1"
                >

                  <div className="mb-2 flex h-6 items-center justify-between">
                    <h4 className="truncate pr-2 text-[15px] font-medium">
                      {item?.card_title || "NO NAME"}
                    </h4>

                    <input
                      type="checkbox"
                      checked={item?.card_status === "Y"}
                      disabled={isUpdating}
                      onChange={() => handleToggleStatus(item)}
                      className={
                        isUpdating
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </div>

                  {/* =====================================
                        BANNER / CAROUSEL PREVIEW
                    ===================================== */}

                  <div
                    className="relative h-[125px] w-full cursor-pointer overflow-hidden rounded-md bg-gray-100"
                    onClick={() => openPreview(item?.card_htmlcode)}
                  >
                    <iframe
                      key={`${item.id}-${item.card_htmlcode?.length || 0}`}
                      title={`preview-${item.id}`}
                      srcDoc={getPreviewHTML(item?.card_htmlcode || "")}
                      scrolling="no"
                      className="pointer-events-none block h-full w-full border-none"
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        border: "none",
                        overflow: "hidden",
                        background: "transparent",
                      }}
                    />
                  </div>

                  {/* =====================================
                        FOOTER
                    ===================================== */}

                  <div className="mt-3 flex h-9 items-center justify-between">
                    {/* VIEW */}

                    <button
                      type="button"
                      onClick={() => openPreview(item?.card_htmlcode)}
                      className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
                    >
                      <Eye size={17} />
                    </button>

                    {/* DELETE */}

                    <button
                      type="button"
                      onClick={() =>
                        setDeleteModal({
                          show: true,
                          item,
                        })
                      }
                      className="flex h-8 w-8 items-center justify-center rounded text-red-500 hover:bg-red-50"
                    >
                      <Trash size={17} />
                    </button>

                    {/* DEFAULT */}

                    <button
                      type="button"
                      className="rounded-full border px-3 py-1 text-[13px] text-green-600"
                    >
                      Set Default
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* =================================================
            PAGINATION
        ================================================= */}

        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            className="rounded border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            type="button"
            disabled={totalPages === 0 || currentPage >= totalPages}
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            className="rounded border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>

        {/* =================================================
            FULL PREVIEW MODAL
        ================================================= */}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
            <div className="w-[80%] rounded-lg bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h5 className="font-semibold">Banner Preview</h5>

                <button type="button" onClick={closePreview}>
                  ✕
                </button>
              </div>

              <div className="h-[70vh] w-full overflow-hidden rounded-md bg-gray-100">
                <iframe
                  key={`modal-${previewHTML.length}`}
                  title="full-banner-preview"
                  srcDoc={getPreviewHTML(previewHTML)}
                  scrolling="no"
                  className="block h-full w-full border-none"
                  style={{
                    width: "100%",

                    height: "100%",

                    border: "none",

                    overflow: "hidden",

                    background: "transparent",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            DELETE MODAL
        ================================================= */}

        {deleteModal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
            <div className="w-[360px] rounded-xl bg-white p-5 shadow-xl">
              <h3 className="mb-2 text-lg font-semibold text-gray-800">
                Delete Banner
              </h3>

              <p className="mb-5 text-sm text-gray-500">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-gray-700">
                  {deleteModal.item?.card_title || "this banner"}
                </span>
                ?
              </p>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => {
                    if (isDeleting) {
                      return;
                    }

                    setDeleteModal({
                      show: false,
                      item: null,
                    });
                  }}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="rounded-lg bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isDeleting ? "Deleting..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default BannerLibrary;
